import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatDate, formatDateTime, plural } from '../lib/format'
import { useAuth } from '../lib/auth'
import type { Advisory, Alert, ConfigBackup, Customer, Device, Overview } from '../lib/types'
import { StatusBadge } from '../components/Status'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { InventarBericht } from '../components/Inventar'
import { Monatsbericht } from '../components/Monatsbericht'
import { Nis2Nachweis } from '../components/Nis2Nachweis'
import { useRouterState } from '@tanstack/react-router'

interface Props {
  customerSlug: string | null
}

/** Zeiträume für die Auswertung. */
const PERIODS = [
  { key: '30', label: 'letzte 30 Tage' },
  { key: '90', label: 'letztes Quartal' },
  { key: '365', label: 'letzte 12 Monate' },
] as const

/** Der Beginn des Auswertungszeitraums. */
export function periodStart(days: number, now = new Date()): Date {
  return new Date(now.getTime() - days * 86_400_000)
}

/** Alarme, die im Zeitraum aufgetreten sind. */
export function alertsInPeriod(alerts: Alert[], from: Date): Alert[] {
  return alerts.filter((a) => Date.parse(a.opened_at) >= from.getTime())
}

interface Grouped {
  key: string
  device: string
  title: string
  count: number
  last: string
  critical: boolean
}

/**
 * Gleichartige Alarme desselben Geräts werden zusammengefasst. Im
 * Kundengespräch zählt "zwölfmal derselbe Ausfall", nicht zwölf Zeilen.
 */
export function groupAlerts(alerts: Alert[]): Grouped[] {
  const byKey = new Map<string, Grouped>()
  for (const a of alerts) {
    const device = a.device_name || a.site_name || a.customer_name || 'ohne Gerät'
    const key = `${device}\u0000${a.title}`
    const row = byKey.get(key) ?? {
      key,
      device,
      title: a.title,
      count: 0,
      last: a.opened_at,
      critical: false,
    }
    row.count += 1
    if (Date.parse(a.opened_at) > Date.parse(row.last)) row.last = a.opened_at
    if (a.severity === 'crit') row.critical = true
    byKey.set(key, row)
  }
  return [...byKey.values()].sort(
    (a, b) => b.count - a.count || Date.parse(b.last) - Date.parse(a.last),
  )
}

/** Geräte nach Art zählen, größte Gruppe zuerst. */
export function countBy(devices: Device[], field: 'type' | 'vendor'): { name: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const d of devices) {
    const name = d[field] || 'ohne Angabe'
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/**
 * Berichte für das Kundengespräch. Die Seite rechnet aus den Daten, die die
 * Netzwarte ohnehin hält: Bestand, Verfügbarkeit, Alarme und
 * Konfigurationsänderungen im gewählten Zeitraum.
 */
export function BerichtePage({ customerSlug }: Props) {
  // Der Link aus der Bericht-Mail nennt den Monat.
  const monatAusAdresse = useRouterState({
    select: (s) => (s.location.search as Record<string, unknown>).monat,
  })
  const [ansicht, setAnsicht] = useState<'monat' | 'zeitraum' | 'inventar' | 'nis2'>(customerSlug ? 'monat' : 'zeitraum')
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
    enabled: Boolean(customerSlug),
  })
  const customer = customers.data?.find((c) => c.slug === customerSlug)

  return (
    <>
      <div role="tablist" aria-label="Art des Berichts" className="reiterleiste no-print">
        {(
          [
            ['monat', 'Monatsbericht'],
            ['zeitraum', 'Zeitraum'],
            ['inventar', 'Inventar'],
            ['nis2', 'NIS2-Nachweis'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={ansicht === key}
            className="reiter"
            onClick={() => setAnsicht(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {ansicht === 'zeitraum' ? (
        <ZeitraumBericht customerSlug={customerSlug} />
      ) : ansicht === 'inventar' ? (
        customerSlug && !customer ? (
          <Loading what="Den Kunden" />
        ) : (
          <InventarBericht customer={customer} />
        )
      ) : ansicht === 'nis2' && customer ? (
        <Nis2Nachweis customer={customer} />
      ) : customer ? (
        <Monatsbericht
          customer={customer}
          monatVorwahl={typeof monatAusAdresse === 'string' ? monatAusAdresse : undefined}
        />
      ) : customerSlug ? (
        <Loading what="Den Kunden" />
      ) : (
        <EmptyState
          title="Einen Kunden wählen"
          hint={`${ansicht === 'nis2' ? 'Der NIS2-Nachweis' : 'Der Monatsbericht'} gilt je Kunde: oben links den Kunden wählen.`}
        />
      )}
    </>
  )
}

/**
 * Bericht über einen gleitenden Zeitraum aus dem, was die Oberfläche ohnehin
 * lädt: Bestand, Zustand jetzt, Alarme und Konfigurationsänderungen.
 */
function ZeitraumBericht({ customerSlug }: Props) {
  const [period, setPeriod] = useState<string>('30')
  const { can } = useAuth()
  const days = Number(period)
  const from = periodStart(days)

  const overview = useQuery({
    queryKey: ['overview', customerSlug],
    queryFn: ({ signal }) => apiGet<Overview>('/overview', signal),
  })
  const devices = useQuery({
    queryKey: ['devices', customerSlug],
    queryFn: ({ signal }) => apiGet<Device[]>('/devices', signal),
  })
  // Alle Alarme, auch die geschlossenen: ein Bericht über nur offene Alarme
  // wäre für einen Rückblick wertlos.
  const alerts = useQuery({
    queryKey: ['alerts', 'bericht', customerSlug],
    queryFn: ({ signal }) => apiGet<Alert[]>('/alerts?alle=1&anzahl=1000', signal),
  })
  const configs = useQuery({
    queryKey: ['configs', 'bericht', customerSlug],
    queryFn: ({ signal }) => apiGet<ConfigBackup[]>('/configs?geaendert=1&anzahl=500', signal),
    enabled: can('config.view'),
  })
  const advisories = useQuery({
    queryKey: ['advisories'],
    queryFn: ({ signal }) => apiGet<Advisory[]>('/advisories', signal),
  })
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
    enabled: Boolean(customerSlug),
  })

  if (overview.isError) return <ErrorBox error={overview.error} what="Der Bericht" />
  if (overview.isPending || devices.isPending) return <Loading what="Den Bericht" />

  const customerName = customerSlug
    ? (customers.data?.find((c) => c.slug === customerSlug)?.name ?? customerSlug)
    : 'Alle Kunden'
  const deviceList = devices.data ?? []
  const periodAlerts = alertsInPeriod(alerts.data ?? [], from)
  const criticalAlerts = periodAlerts.filter((a) => a.severity === 'crit')
  const grouped = groupAlerts(periodAlerts)
  const changes = (configs.data ?? []).filter(
    (b) => b.changed && Date.parse(b.taken_at) >= from.getTime(),
  )
  const unreviewed = changes.filter((b) => b.review_state === 'offen')
  const relevantAdvisories = (advisories.data ?? []).filter((a) => (a.betroffene_geraete ?? 0) > 0)
  const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? `letzte ${days} Tage`

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Berichte</h1>
          <div className="page-sub">
            {customerName} · {periodLabel} · ab {formatDate(from.toISOString())}
          </div>
        </div>
        <div className="row no-print" style={{ gap: 8 }}>
          <label className="sr-only" htmlFor="bericht-zeitraum">
            Zeitraum
          </label>
          <select
            id="bericht-zeitraum"
            className="input"
            style={{ width: 190, height: 32 }}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
          <button type="button" className="btn" onClick={() => window.print()}>
            Drucken
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Geräte" value={deviceList.length} sub={plural(overview.data.kpi.sites, 'Standort', 'Standorte')} />
        <Kpi
          label="Nicht erreichbar"
          value={overview.data.kpi.devices_down}
          sub="Stand jetzt"
          tone={overview.data.kpi.devices_down > 0 ? 'crit' : 'ok'}
        />
        <Kpi
          label="Alarme im Zeitraum"
          value={periodAlerts.length}
          sub={`${criticalAlerts.length} kritisch`}
          tone={criticalAlerts.length > 0 ? 'warn' : 'ok'}
        />
        <Kpi
          label="Config-Änderungen"
          value={changes.length}
          sub={can('config.view') ? `${unreviewed.length} ungeprüft` : 'kein Einblick'}
          tone={unreviewed.length > 0 ? 'warn' : 'ok'}
        />
        <Kpi
          label="Firmware-Hinweise"
          value={relevantAdvisories.length}
          sub="mit betroffenen Geräten"
          tone={relevantAdvisories.length > 0 ? 'warn' : 'ok'}
        />
      </div>

      <div className="row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <section className="card" style={{ flexGrow: 1, minWidth: 320 }}>
          <div className="card-head">
            <h2 className="card-title">Bestand nach Art</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {plural(deviceList.length, 'Gerät', 'Geräte')}
            </span>
          </div>
          {deviceList.length === 0 ? (
            <div className="empty">
              <span>Für diesen Kunden ist noch kein Gerät angelegt.</span>
            </div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Art</th>
                  <th>Anzahl</th>
                </tr>
              </thead>
              <tbody>
                {countBy(deviceList, 'type').map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td className="mono">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card" style={{ flexGrow: 1, minWidth: 320 }}>
          <div className="card-head">
            <h2 className="card-title">Verfügbarkeit je Standort</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              OK · Warn · Down
            </span>
          </div>
          {overview.data.sites.length === 0 ? (
            <div className="empty">
              <span>Noch kein Standort angelegt.</span>
            </div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Standort</th>
                  <th>Agent</th>
                  <th>Geräte</th>
                </tr>
              </thead>
              <tbody>
                {overview.data.sites.map((s) => (
                  <tr key={s.site_id}>
                    <td>{s.site_name}</td>
                    <td>
                      {s.agent_online ? (
                        <StatusBadge state="ok" label="online" />
                      ) : (
                        <StatusBadge state="crit" label="offline" />
                      )}
                    </td>
                    <td className="mono">
                      <span style={{ color: 'var(--ok)' }}>{s.devices_ok}</span>{' '}
                      <span style={{ color: 'var(--warn)' }}>{s.devices_warn}</span>{' '}
                      <span style={{ color: 'var(--crit)' }}>{s.devices_down}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Häufigste Alarme</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            {periodLabel}, gleichartige Alarme zusammengefasst
          </span>
        </div>
        {alerts.isPending ? (
          <div className="card-body">
            <Loading what="Alarme" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="empty">
            <strong>Kein Alarm im Zeitraum</strong>
            <span>In diesem Zeitraum ist nichts aufgelaufen.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Alarm</th>
                <th>Anzahl</th>
                <th>Zuletzt</th>
              </tr>
            </thead>
            <tbody>
              {grouped.slice(0, 15).map((row) => (
                <tr key={row.key}>
                  <td className="mono">{row.device}</td>
                  <td>
                    {row.critical ? <StatusBadge state="crit" label="kritisch" /> : null} {row.title}
                  </td>
                  <td className="mono">{row.count}</td>
                  <td className="muted">{formatDateTime(row.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {can('config.view') ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Konfigurationsänderungen</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {periodLabel}
            </span>
          </div>
          {changes.length === 0 ? (
            <div className="empty">
              <span>In diesem Zeitraum wurde keine Konfiguration geändert.</span>
            </div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Gerät</th>
                  <th>Zeitpunkt</th>
                  <th>Umfang</th>
                  <th>Prüfung</th>
                </tr>
              </thead>
              <tbody>
                {changes.slice(0, 15).map((b) => (
                  <tr key={b.id}>
                    <td className="mono">{b.device_name}</td>
                    <td className="muted">{formatDateTime(b.taken_at)}</td>
                    <td className="mono">
                      +{b.added} −{b.removed}
                    </td>
                    <td>
                      {b.review_state === 'offen' ? (
                        <StatusBadge state="warn" label="ungeprüft" />
                      ) : b.review_state === 'beanstandet' ? (
                        <StatusBadge state="crit" label="beanstandet" />
                      ) : (
                        <StatusBadge state="ok" label="geprüft" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Firmware-Hinweise</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            nur Hinweise mit betroffenen Geräten
          </span>
        </div>
        {relevantAdvisories.length === 0 ? (
          <div className="empty">
            <span>Kein Hinweis betrifft ein Gerät dieses Bestands.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Hersteller</th>
                <th>Hinweis</th>
                <th>Schwere</th>
                <th>Geräte</th>
              </tr>
            </thead>
            <tbody>
              {relevantAdvisories.map((a) => (
                <tr key={a.id}>
                  <td>{a.vendor}</td>
                  <td>
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer noopener">
                        {a.titel}
                      </a>
                    ) : (
                      a.titel
                    )}
                  </td>
                  <td>
                    <StatusBadge
                      state={a.schwere === 'crit' ? 'crit' : a.schwere === 'warn' ? 'warn' : 'off'}
                      label={a.schwere === 'crit' ? 'kritisch' : a.schwere === 'warn' ? 'Warnung' : 'Hinweis'}
                    />
                  </td>
                  <td className="mono">{a.betroffene_geraete}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

function Kpi({
  label,
  value,
  sub,
  tone = 'off',
}: {
  label: string
  value: number
  sub: string
  tone?: 'ok' | 'warn' | 'crit' | 'off'
}) {
  const colors = { ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)', off: 'var(--text)' }
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value" style={{ color: colors[tone] }}>
        {value}
      </span>
      <span className="kpi-sub">{sub}</span>
    </div>
  )
}
